import Date from "../Inputs/Date";
import { Backdrop, Button, CircularProgress, Grid } from "@mui/material";
import { useSelector } from "react-redux";
import { BudgetItem, BuildReportColumnLabels, CurrentUser, CustomReportFilter, CustomReportSettings, DateTime, FamilyBucketLabel, FamilyBucketPluralLabel, GetEnabledBucketDefinitions, Grouping, GroupTransactionTotal, TransactionItem, USCurrency } from "../../types";
import ReportPreviewTables from "../Reports/ReportPreviewTables";
import { buildReportPreview } from "../../features/Reports/preview";
import { useMemo, useState } from "react";
import ModalHeader from "./ModalHeader";
import ModalFooterCustom from "./ModalFooterCustom";
import { DownloadPdf } from "../../features/ReportService";
import { useItems } from "../../features/Fetch/Items";

const ExpensesReport = (props: any) => {
	let budgets: BudgetItem[] = props.Budgets;
	let transactions: TransactionItem[] = props.Transactions;
	let user: CurrentUser = useSelector((state: any) => state.userData.value);
	let budgetLabel = FamilyBucketLabel(user, "budget", "Budget");
	let budgetPluralLabel = FamilyBucketPluralLabel(user, "budget", "Budgets");

	const bucketColumnKeys = useMemo(() => GetEnabledBucketDefinitions(user)
		.filter(d => d.bucketTypeKey?.toLowerCase() !== 'budget')
		.map(d => `Bucket:${d.bucketTypeKey}`), [user]);
	const columns = useMemo(() => ["Date", "Name", "Merchant", ...bucketColumnKeys, "Description", "Amount"], [bucketColumnKeys]);
	const reportTitle = `${!props.IsMileage ? "Spending" : "Mileage"} By ${budgetLabel}`;

	let [startDate, setStartDate] = useState(props.StartDate as DateTime);
	let [endDate, setEndDate] = useState(props.EndDate as DateTime);
	let [overlay, setOverlay] = useState(false);
	const { groups: budgetGroups } = buildReportPreview({
		transactions: transactions,
		budgets: budgets,
		subBudgets: budgets.flatMap(b => b.subBudgets ?? []),
		settings: {
			title: reportTitle,
			columns,
			grouping: 'Budget',
			filter: {
				startDate: startDate,
				endDate: endDate,
				includeSpending: !props.IsMileage,
				includeMiles: props.IsMileage,
				includeCheckRequests: false,
				combineSplitBudgets: false,
				includeReceipts: false,
				groupReceipts: false
			} as CustomReportFilter
		} as CustomReportSettings
	});

	let repo = useItems();

	return (<>
		<ModalHeader Title={`All ${budgetPluralLabel}`} CanDelete={false} Close={props.Close} />
		<div className="modal-body mt-5">
			<Grid container spacing={1}>
				<Grid item xs={12}>
					<Button onClick={() => Export(budgetGroups)}>
						Download Report
					</Button>
				</Grid>
				<Date Size={4} Label="From" Value={startDate} OnChange={(v: any) => setStartDate(new DateTime(v))} />
				<Date Size={4} Label="To" Value={endDate} OnChange={(v: any) => setEndDate(new DateTime(v))} />
				<Grid item xs={4} >
					<h1 className="report-total">{USCurrency(GroupTransactionTotal(budgetGroups))}</h1>
				</Grid>
			</Grid>

			<ReportPreviewTables
				groups={budgetGroups}
				columns={columns}
				grouping="Budget"
			/>
		</div>
		<ModalFooterCustom HideSave={true} Close={props.Close} />
		<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={overlay}>
			<CircularProgress />
		</Backdrop>
	</>);


	async function Export(items: Grouping[]) {
		setOverlay(true);
		setTimeout(() => GenerateReport(items), 50);
	}

	async function GenerateReport(_items: Grouping[]) {
		let settings: CustomReportSettings = {
			title: reportTitle,
			columns: columns,
			columnLabels: BuildReportColumnLabels(user, columns),
			grouping: 'Budget',
			filter: {
				startDate: startDate,
				endDate: endDate,
				includeSpending: !props.IsMileage,
				includeMiles: props.IsMileage,
				includeCheckRequests: false,
				combineSplitBudgets: false,
				includeReceipts: false,
				groupReceipts: false
			} as CustomReportFilter
		}
		let file = await repo.GetReportPdfAsync(settings);
		DownloadPdf(await file.blob() as Blob, reportTitle);
		setOverlay(false);
	}
}

export default ExpensesReport;